import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios, { API } from '../utils/api';
import { usePoints } from '../context/PointsContext';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RefreshCw, ImagePlus, Wand2, Send, Sparkles, Mic, MicOff, Tag } from 'lucide-react';
import { useVoiceToText } from '../hooks/useVoiceToText';
import notify from '../utils/notify';
import { callWithRetry } from '../utils/retry';
import ImageUploadPicker from '../components/shared/ImageUploadPicker';

const TEAL = '#4DB6AC';
const TAG_TEAL = '#47b8a3';
const TAG_BG = '#e8f7f5';
const TAG_TEXT = '#1a7a6e';

const iconBtnStyle = {
  width: 40, height: 40, minWidth: 40, flexShrink: 0,
  borderRadius: 10, border: 'none',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', transition: 'all 0.18s cubic-bezier(.22,1,.36,1)',
};

export const CreatePostPage = ({ TopHeader, BottomNav, useAuth }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { refreshPoints } = usePoints();
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // `selectedFile` now only ever holds an AI-generated blob (see
  // handleConfirmAIImage). Manually-picked files are uploaded immediately
  // by ImageUploadPicker; the resulting server URL lands in `mediaUrl`.
  const [selectedFile, setSelectedFile] = useState(null);
  const [mediaUrl, setMediaUrl] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  // Tracks whether an ImageUploadPicker upload is currently in flight.
  // Gates the submit button and surfaces an inline message if the user
  // tries to submit before the POST /api/upload response lands.
  const [uploading, setUploading] = useState(false);
  const [uploadStillInFlightError, setUploadStillInFlightError] = useState('');
  const [showCelebration, setShowCelebration] = useState(false);
  const [rephrasing, setRephrasing] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [aiImagePreview, setAiImagePreview] = useState(null);
  const [imageGenError, setImageGenError] = useState('');
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState(new Set());
  const [loadingTags, setLoadingTags] = useState(false);
  const textareaRef = useRef(null);

  const voicePost = useVoiceToText({
    onTranscript: (text) => setContent(prev => prev ? `${prev} ${text}` : text),
  });

  const actionData = location.state?.actionData;

  // Prefill support: callers can navigate here with
  //   navigate('/create-post', { state: { prefill: { content, tags } } })
  // to seed the composer with a suggested draft (currently used by the
  // ME-page "Log Progress" success screen so users can go from logging
  // → sharing in one deliberate step). The user is free to edit or
  // discard everything before submitting; nothing is auto-posted.
  useEffect(() => {
    const prefill = location.state?.prefill;
    if (!prefill) return;
    if (typeof prefill.content === 'string' && prefill.content) {
      setContent(prefill.content);
    }
    if (Array.isArray(prefill.tags) && prefill.tags.length) {
      // Reuse the existing selected-tag machinery — clean strings and
      // dedupe, then hand them to both `selectedTags` (for the pill UI)
      // and `suggestedTags` (so the pills render immediately without
      // waiting for the AI suggest-tags call).
      const clean = Array.from(new Set(prefill.tags.map((t) => String(t).replace(/^#/, '').trim()).filter(Boolean)));
      setSuggestedTags(clean);
      setSelectedTags(new Set(clean));
    }
    // Only run when this component mounts with a fresh navigation state.
    // eslint-disable-next-line
  }, []);

  // --- Handlers ---

  const handleRephrase = async () => {
    if (!content.trim()) return;
    setRephrasing(true);
    try {
      // Auto-retry once on failure — the AI provider flakes intermittently.
      const res = await callWithRetry(() => axios.post(`${API}/rephrase-text`, { text: content }));
      setContent(res.data.rephrased_text);
    } catch (err) {
      notify.error(err?.response?.data?.detail || 'Failed to rephrase. Please try again.');
    }
    finally { setRephrasing(false); }
  };

  const handleFetchTags = async () => {
    const plain = content.trim();
    if (!plain || plain.length < 10) return;
    setLoadingTags(true);
    try {
      const res = await axios.post(`${API}/suggest-tags`, { post_text: plain });
      const tags = (res.data.tags || []).slice(0, 5);
      setSuggestedTags(tags);
      // keep only previously-selected that still appear in new suggestions
      setSelectedTags(prev => {
        const next = new Set();
        for (const t of prev) { if (tags.includes(t)) next.add(t); }
        return next;
      });
    } catch { setSuggestedTags([]); }
    finally { setLoadingTags(false); }
  };

  const toggleTag = (tag) => {
    const hashtag = ` #${tag}`;
    if (selectedTags.has(tag)) {
      setSelectedTags(prev => { const n = new Set(prev); n.delete(tag); return n; });
      setContent(c => c.replace(hashtag, '').replace(`#${tag}`, '').trimEnd());
    } else {
      setSelectedTags(prev => new Set([...prev, tag]));
      setContent(c => c.trimEnd() + hashtag);
    }
  };

  const handleRemoveFile = () => { setSelectedFile(null); setMediaUrl(''); setPreviewUrl(''); setAiImagePreview(null); setUploadProgress(0); };

  const handleGenerateAIImage = async () => {
    setImageGenError('');
    if (!content.trim()) { setImageGenError('Please write something first so we can generate a relevant image.'); return; }
    setGeneratingImage(true); setAiImagePreview(null);
    try {
      const res = await axios.post(`${API}/generate-image`, { post_text: content });
      setAiImagePreview(`data:${res.data.mime_type || 'image/png'};base64,${res.data.image_data}`);
    } catch (err) { setImageGenError(err.response?.data?.detail || 'Failed to generate image. Please try again.'); }
    finally { setGeneratingImage(false); }
  };

  const handleConfirmAIImage = () => {
    if (!aiImagePreview) return;
    setPreviewUrl(aiImagePreview); setSelectedFile(null);
    fetch(aiImagePreview).then(r => r.blob()).then(blob => { setSelectedFile(new File([blob], 'ai-generated.png', { type: 'image/png' })); });
    setAiImagePreview(null); setImageGenError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Block if a manual upload is still in flight — otherwise mediaUrl
    // would still be '' and the post would ship without the image the
    // user just picked.
    if (uploading) {
      setUploadStillInFlightError('Your image is still uploading — please wait a moment and try again.');
      return;
    }
    setUploadStillInFlightError('');
    setSubmitting(true);
    try {
      // mediaUrl is already set by ImageUploadPicker for manual uploads.
      // selectedFile only holds AI-derived blobs (see handleConfirmAIImage)
      // which still need a submit-time upload — that path is unchanged.
      let finalMediaUrl = mediaUrl;
      if (!finalMediaUrl && selectedFile) {
        const fd = new FormData(); fd.append('file', selectedFile);
        const upRes = await axios.post(`${API}/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (p) => setUploadProgress(Math.round((p.loaded * 100) / p.total))
        });
        finalMediaUrl = upRes.data.url;
      }
      // Extract tags from content (anything starting with #)
      const tagMatches = content.match(/#(\w+)/g) || [];
      const tags = tagMatches.map(t => t.slice(1));
      await axios.post(`${API}/posts`, { content, media_url: finalMediaUrl || null, tags });
      refreshPoints();
      if (actionData) { localStorage.setItem('completedFirstAction', 'true'); setShowCelebration(true); }
      else { navigate('/', { replace: true }); }
    } catch (error) { notify.error('Failed to create post: ' + (error.response?.data?.detail || error.message)); }
    finally { setSubmitting(false); }
  };

  const submitBtnStyle = {
    width: '100%', padding: '14px 16px', background: submitting ? '#9CA3AF' : TEAL, color: '#fff',
    border: 'none', borderRadius: 10, cursor: submitting ? 'not-allowed' : 'pointer',
    fontWeight: 600, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    boxShadow: '0px 4px 8px rgba(77, 182, 172, 0.3)', transition: 'all 0.2s ease',
    userSelect: 'none', WebkitTapHighlightColor: 'transparent',
  };

  return (
    <>
      {/* Celebration modal */}
      <AnimatePresence>
        {showCelebration && actionData && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              style={{ background: 'white', borderRadius: 20, padding: 32, maxWidth: 340, width: '90%', textAlign: 'center' }}>
              <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>First Action Complete!</h2>
              <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 20 }}>
                You earned <strong style={{ color: TEAL }}>+{actionData.reward_points || 30} stars</strong> for sharing!
              </p>
              <button data-testid="view-rewards-btn" onClick={() => navigate('/rewards', { replace: true })}
                style={{ ...submitBtnStyle, marginBottom: 12, fontSize: 16 }}>
                View Rewards
              </button>
              <button onClick={() => navigate('/', { replace: true })}
                style={{ width: '100%', padding: 14, background: '#F1F3F5', color: '#6C757D', border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: 'pointer' }}>
                Back to WE Page
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="page-container">
        <TopHeader title="Share Post" showBack={true} />
        <div className="content-wrapper">
          <div className="content-section">
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '0 2px' }}>

              {/* --- Textarea --- */}
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#1F2937', marginBottom: 8 }}>
                  What&#39;s on your mind?
                </label>
                <textarea
                  ref={textareaRef}
                  className="form-input" placeholder="Just completed my first 10k steps challenge!"
                  value={content} onChange={(e) => setContent(e.target.value)}
                  rows={5} required data-testid="input-content"
                  style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
                />

                {/* --- Toolbar strip --- */}
                <div data-testid="toolbar-strip" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                  {/* AI Rephrase */}
                  <button type="button" data-testid="rephrase-btn" onClick={handleRephrase}
                    disabled={rephrasing || !content.trim()}
                    style={{
                      ...iconBtnStyle,
                      background: rephrasing ? TEAL : '#F3F4F6',
                      color: rephrasing ? '#fff' : !content.trim() ? '#9CA3AF' : '#374151',
                      cursor: (rephrasing || !content.trim()) ? 'not-allowed' : 'pointer',
                      boxShadow: rephrasing ? `0 0 0 3px ${TEAL}25` : 'none',
                    }}>
                    {rephrasing
                      ? <RefreshCw width={16} height={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                      : <Wand2 width={16} height={16} strokeWidth={2} />}
                  </button>

                  {/* Mic */}
                  <button type="button" data-testid="voice-mic-btn" onClick={voicePost.toggleListening}
                    disabled={voicePost.voiceRephrasing}
                    style={{
                      ...iconBtnStyle,
                      background: voicePost.isListening ? '#EF4444' : voicePost.voiceRephrasing ? '#E5E7EB' : '#F3F4F6',
                      color: voicePost.isListening ? '#fff' : voicePost.voiceRephrasing ? '#9CA3AF' : '#374151',
                      cursor: voicePost.voiceRephrasing ? 'not-allowed' : 'pointer',
                      animation: voicePost.isListening ? 'pulse-mic 1.2s infinite' : 'none',
                    }}>
                    {voicePost.voiceRephrasing
                      ? <RefreshCw width={16} height={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                      : voicePost.isListening
                        ? <MicOff width={16} height={16} strokeWidth={2} />
                        : <Mic width={16} height={16} strokeWidth={2} />}
                  </button>

                  {/* Language toggle */}
                  <div data-testid="lang-toggle-pill" style={{
                    display: 'inline-flex', height: 40, flexShrink: 0,
                    borderRadius: 20, overflow: 'hidden',
                    border: `1.5px solid ${TEAL}30`, background: '#fff',
                  }}>
                    {[{ id: 'en-IN', label: 'EN' }, { id: 'hi-IN', label: '\u0939\u093F' }].map(l => (
                      <button key={l.id} type="button" data-testid={`voice-lang-${l.id.toLowerCase()}`}
                        onClick={() => voicePost.setSelectedLang(l.id)}
                        style={{
                          width: 44, height: '100%', border: 'none',
                          fontSize: 12, fontWeight: 700, cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          background: voicePost.selectedLang === l.id ? TEAL : 'transparent',
                          color: voicePost.selectedLang === l.id ? '#fff' : '#9CA3AF',
                          transition: 'all 0.15s',
                        }}>
                        {l.label}
                      </button>
                    ))}
                  </div>

                  {/* Tag button */}
                  <button type="button" data-testid="tag-suggest-btn" onClick={handleFetchTags}
                    disabled={loadingTags || !content.trim() || content.trim().length < 10}
                    style={{
                      ...iconBtnStyle,
                      background: loadingTags ? TEAL : '#F3F4F6',
                      color: loadingTags ? '#fff' : (!content.trim() || content.trim().length < 10) ? '#9CA3AF' : '#374151',
                      cursor: (loadingTags || !content.trim() || content.trim().length < 10) ? 'not-allowed' : 'pointer',
                      boxShadow: loadingTags ? `0 0 0 3px ${TEAL}25` : 'none',
                    }}>
                    {loadingTags
                      ? <RefreshCw width={16} height={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                      : <Tag width={16} height={16} strokeWidth={2} />}
                  </button>
                </div>

                {/* --- Tag suggestion pills --- */}
                {loadingTags && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                    {[1,2,3,4].map(i => (
                      <div key={i} style={{ width: 72, height: 32, borderRadius: 20,
                        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                        backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }} />
                    ))}
                  </div>
                )}
                {!loadingTags && suggestedTags.length > 0 && (
                  <div data-testid="tag-suggestions" style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                    {suggestedTags.map((tag) => {
                      const isSelected = selectedTags.has(tag);
                      return (
                        <button type="button" key={tag} data-testid={`tag-chip-${tag}`}
                          onClick={() => toggleTag(tag)}
                          style={{
                            padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500,
                            cursor: 'pointer', transition: 'all 0.2s',
                            background: isSelected ? TAG_TEAL : TAG_BG,
                            color: isSelected ? '#fff' : TAG_TEXT,
                            border: `1.5px solid ${TAG_TEAL}`,
                          }}>
                          #{tag}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* --- Add Photo --- */}
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#1F2937', marginBottom: 8 }}>Add Photo</label>
                <div style={{ display: 'flex', gap: 12 }}>
                  <ImageUploadPicker
                    testId="choose-photo-btn"
                    disabled={generatingImage}
                    style={{ flex: 1 }}
                    onUploadStart={(dataUrl) => { setPreviewUrl(dataUrl); setAiImagePreview(null); setSelectedFile(null); setUploading(true); setUploadStillInFlightError(''); }}
                    onProgress={setUploadProgress}
                    onUploadComplete={(url) => { setMediaUrl(url); setPreviewUrl(url); setUploadProgress(0); setUploading(false); setUploadStillInFlightError(''); }}
                    onUploadError={() => { setPreviewUrl(''); setMediaUrl(''); setUploadProgress(0); setUploading(false); }}
                  />
                  <button type="button" data-testid="generate-ai-image-btn"
                    onClick={handleGenerateAIImage} disabled={generatingImage}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '12px 16px', background: '#E0F2F1',
                      color: TEAL, border: `1.5px solid ${TEAL}`, borderRadius: 12,
                      fontSize: 14, fontWeight: 600, cursor: generatingImage ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}
                    onMouseEnter={(e) => { if (!generatingImage) e.currentTarget.style.background = '#B2DFDB'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#E0F2F1'; }}>
                    {generatingImage ? <RefreshCw width={18} height={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles width={18} height={18} />}
                    {generatingImage ? 'Generating...' : 'Generate'}
                  </button>
                </div>

                {imageGenError && (
                  <div data-testid="image-gen-error" style={{ marginTop: 10, padding: '10px 14px', background: '#FEF2F2',
                    border: '1px solid #FECACA', borderRadius: 10, fontSize: 13, color: '#B91C1C' }}>
                    {imageGenError}
                  </div>
                )}

                {generatingImage && (
                  <div data-testid="ai-image-loading" style={{ marginTop: 16, height: 200, background: '#F9FAFB', borderRadius: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px dashed #E5E7EB', overflow: 'hidden', position: 'relative' }}>
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, transparent, rgba(93,191,173,0.08), transparent)', animation: 'shimmer 1.5s infinite', backgroundSize: '200% 100%' }} />
                    <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', color: TEAL, marginBottom: 8, position: 'relative' }} />
                    <p style={{ fontSize: 13, color: '#6B7280', position: 'relative' }}>Generating image...</p>
                    <style>{`@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }`}</style>
                  </div>
                )}

                {aiImagePreview && !generatingImage && (
                  <div style={{ marginTop: 12 }}>
                    <img src={aiImagePreview} alt="AI Generated" style={{ width: '100%', maxHeight: 300, objectFit: 'cover', borderRadius: 12 }} />
                    <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
                      <button type="button" data-testid="confirm-ai-image-btn" onClick={handleConfirmAIImage}
                        style={{ flex: 1, padding: '14px 16px', background: TEAL, color: '#fff', border: 'none',
                          borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                        Use This Image
                      </button>
                      <button type="button" data-testid="regenerate-ai-image-btn" onClick={handleGenerateAIImage}
                        style={{ flex: 1, padding: '14px 16px', background: '#F3F4F6', color: '#374151', border: 'none',
                          borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)' }}>
                        Regenerate
                      </button>
                    </div>
                  </div>
                )}

                {previewUrl && (
                  <div style={{ position: 'relative', marginTop: 12, borderRadius: 12, overflow: 'hidden' }}>
                    <img src={previewUrl} alt="Preview" style={{ width: '100%', maxHeight: 400, objectFit: 'cover', borderRadius: 12 }} />
                    <button type="button" onClick={handleRemoveFile} data-testid="remove-image-btn"
                      style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(0,0,0,0.6)', color: 'white',
                        border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <X size={16} />
                    </button>
                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 4, background: 'rgba(255,255,255,0.3)' }}>
                        <div style={{ height: '100%', width: `${uploadProgress}%`, background: TEAL, transition: 'width 0.3s' }} />
                      </div>
                    )}
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 8 }}>Supported: JPG, PNG, GIF, WEBP (Max 10MB)</div>
              </div>

              {/* --- Submit --- */}
              {uploadStillInFlightError && (
                <div
                  data-testid="create-post-upload-error"
                  style={{ fontSize: 12, color: '#DC2626', marginBottom: 8, textAlign: 'center' }}
                >
                  {uploadStillInFlightError}
                </div>
              )}
              <button type="submit" data-testid="submit-post-btn" disabled={submitting || uploading}
                style={submitBtnStyle}
                onMouseEnter={!submitting ? (e) => { e.currentTarget.style.background = '#3aa690'; } : undefined}
                onMouseLeave={!submitting ? (e) => { e.currentTarget.style.background = TEAL; } : undefined}>
                {submitting ? 'Sharing...' : (<><Send width={18} height={18} /> Share</>)}
              </button>
            </form>
          </div>
        </div>
        <BottomNav active="home" />
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes pulse-mic { 0%, 100% { box-shadow: 0 0 0 4px rgba(239,68,68,0.2); } 50% { box-shadow: 0 0 0 8px rgba(239,68,68,0.1); } }
      `}</style>
    </>
  );
};
